import React from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import NameCheck from "../components/NameCheck";
import NameCheckFaq from "../components/NameCheckFaq";
import useSeo from "../hooks/useSeo";
import { SITE_URL } from "../lib/site";

const NAME_CHECK_FAQS = [
  {
    q: "How can I check company name availability in India?",
    a: "Use ZOZO Venture's Name Check tool on this page. We verify your proposed company name on the Ministry of Corporate Affairs (MCA) registry, applying MCA's similar-name rules, reserved-word filters and resemblance checks — and email you a complete report within 24 hours.",
  },
  {
    q: "Is startup name availability the same as MCA company name availability?",
    a: "Yes. In India, every Private Limited Company, OPC and LLP needs name approval from the MCA before incorporation. Our Startup Name Availability check is an MCA-style name search that flags rejection risks before you file SPICe+ or RUN.",
  },
  {
    q: "Why should I check trademark availability before registering my company?",
    a: "A company name cleared by MCA can still infringe an existing registered trademark — exposing you to cease-and-desist notices and forced rebranding. We check the Indian Trademark Registry (Class-wise) so you launch on a name you can defend.",
  },
  {
    q: "Do you also check matching domains (.com / .in / .co.in)?",
    a: "Yes. Along with company name availability, we verify whether matching domain extensions (.com / .in / .co.in / .co) are available — so your brand, MCA name and website stay consistent.",
  },
  {
    q: "How long does the name check report take?",
    a: "You'll receive the complete analysis — MCA availability, matching domains, trademark clearance and (if opted-in) founder-name compatibility — within 24 hours over email.",
  },
];

export default function NameCheckPage() {
  useSeo({
    title:
      "Check Company Name Availability — MCA, Domain & Trademark Search | ZOZO Venture",
    description:
      "Check Company Name Availability & Startup Name Availability — verified on MCA, matching domains and Indian trademark registry within 24 hours. By ZOZO Venture.",
    canonical: `${SITE_URL}/name-check`,
    image:
      "https://images.pexels.com/photos/3184418/pexels-photo-3184418.jpeg?auto=compress&cs=tinysrgb&w=1200",
    type: "website",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "WebPage",
        name: "Check Company Name Availability — Startup Name Availability Tool",
        url: `${SITE_URL}/name-check`,
        description:
          "Check Company Name Availability & Startup Name Availability — verified on MCA, matching domains and Indian trademark registry within 24 hours.",
        keywords:
          "Company Name Availability, Check Company Name Availability, Business Name Availability, Startup Name Availability, Domain Name Availability, Check Domain Name Availability, Godaddy, Company Registration, Private Limited Company Registration, Register Company, GST Registration",
        primaryImageOfPage: {
          "@type": "ImageObject",
          url: "https://images.pexels.com/photos/7581127/pexels-photo-7581127.jpeg?auto=compress&cs=tinysrgb&w=1200",
        },
        inLanguage: "en-IN",
        isPartOf: {
          "@type": "WebSite",
          name: "ZOZO Venture",
          url: `${SITE_URL}/`,
        },
      },
      {
        "@context": "https://schema.org",
        "@type": "Service",
        name: "Check Company Name Availability — Startup Name Availability Search",
        serviceType: "Company name and trademark availability search",
        provider: {
          "@type": "Organization",
          name: "ZOZO Venture",
          url: `${SITE_URL}/`,
        },
        areaServed: { "@type": "Country", name: "India" },
        offers: {
          "@type": "Offer",
          price: "0",
          priceCurrency: "INR",
          availability: "https://schema.org/InStock",
          description:
            "MCA company name availability search + matching domain check + Indian trademark clearance, delivered within 24 hours.",
        },
        description:
          "Verify business name availability on the MCA, .com / .in / .co.in domains and the Indian trademark registry before you incorporate or invest in a brand.",
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
          { "@type": "ListItem", position: 2, name: "Name Check", item: `${SITE_URL}/name-check` },
        ],
      },
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: NAME_CHECK_FAQS.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      },
    ],
  });

  return (
    <div
      className="min-h-screen bg-white text-[#0A0A0A] font-body"
      data-testid="name-check-page"
    >
      <Header />
      <main>
        <NameCheck />
        <NameCheckFaq faqs={NAME_CHECK_FAQS} />
      </main>
      <Footer minimal />
    </div>
  );
}
